import React, { useEffect, useState, useRef } from "react";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { AiOutlineClose, AiOutlineEdit, AiOutlineEye } from "react-icons/ai";
import SummaryApi from "../common";
import { useTable, usePagination } from "react-table";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";
import StudentsModal from "../components/StudentsModal";
import FollowUpModal from "../components/FollowupModal";

// ────────────────────────────────────────────────
// THEME — same rules as AllCities.js / InterestedStudents.js
// ────────────────────────────────────────────────
const THEME = {
  primary: "rgb(23, 44, 87)", // #172C57
  primaryLight: "rgba(23, 44, 87, 0.08)",
  actionBlue: "#2563EB",
  actionBlueHover: "#1D4ED8",
  actionYellow: "#D97706",
  actionYellowHover: "#B45309",
  actionPurple: "#7C3AED",
  actionPurpleHover: "#6D28D9",
  actionGreen: "#10B981",
  actionGreenHover: "#059669",
};

// Maps the `filter` query param used by the summary cards
// (e.g. /callback-students?filter=today) to the internal range value.
// This page only shows Call Back students, so the only variable is the date
// range (all-time vs today).
const URL_FILTER_MAP = {
  today: "today",
  callback_today: "today",
  yesterday: "yesterday",
  thisweek: "thisWeek",
  this_week: "thisWeek",
  lastweek: "lastWeek",
  last_week: "lastWeek",
  all: "",
  callback: "",
};

const CallBackStudents = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [studentsData, setStudentsData] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isUpload, setIsUpload] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [filteredStudents, setFilteredStudents] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [collegeSearch, setCollegeSearch] = useState("");
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [dropdownValue, setDropdownValue] = useState("");
  const [minEnrolledDate, setMinEnrolledDate] = useState(null);
  const [maxEnrolledDate, setMaxEnrolledDate] = useState(new Date());
  const [selection, setSelection] = useState({ reason: "", subReason: "" });
  const [lastEditedStudentId, setLastEditedStudentId] = useState(null);
  const tableRef = useRef(null);
  const prevLatestFollowUpId = useRef(null);
  const [highlightTrigger, setHighlightTrigger] = useState(0);
  const prevHighlightTrigger = useRef(0);

  // Holds the range parsed from the URL so it can be applied once data arrives
  const pendingUrlRangeRef = useRef(null);

  const reasons = [
    "Wrong Number",
    "Not Reachable",
    "Number Not Working",
    "Call Declined",
    "Busy",
    "Switched Off",
    "Incoming Calls Stopped",
  ];

  const districts = [
    "Alluri Sitharama Raju", "Anakapalli", "Ananthapuram", "Annamayya", "Bapatla", "CHITTOOR",
    "EAST GODAVARI", "Eluru", "Guntur", "Kakinada", "Konaseema", "Krishna", "Kurnool",
    "Manyam", "Nandyal", "NTR", "Palnadu", "Prakasam", "Srikakulam", "SPS Nellore",
    "Sri Satyasai", "Tirupathi", "Vizianagaram", "Visakhapatnam", "West Godavari", "YSR Kadapa",
  ];

  const [selectedRange, setSelectedRange] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [formData, setFormData] = useState({
    student_name: "",
    gender: "Male",
    father_name: "",
    mobile_number1: "",
    mobile_number2: "",
    email: "",
    aadhar_number: "",
    state: "",
    district: "",
    city: "",
    address: "",
    pincode: "",
    course: "",
    clg_name: "",
    program_type: "",
    ug_pg_course: "",
    call_back_reason: "",
    call_duration: "00:00:00.000000",
    other_reasons: "",
    is_interested: "",
    call_status: "",
    remarks: "",
    prev_call_back_reason: "",
    hours: "00",
    minutes: "00",
    seconds: "00",
    is_jee: "",
    is_jee2: "",
    lead_type: "",
    native_district: "",
    native_address: "",
    next_follow_up_date: "",
  });
  const [updatedData, setUpdatedData] = useState({});
  const [memberType, setMemberType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchModalIsOpen, setSearchModalIsOpen] = useState(false);
  const [searchDistrict, setSearchDistrict] = useState("");
  const [searchCity, setSearchCity] = useState("");
  const [searchCollege, setSearchCollege] = useState("");
  const [followUpModalIsOpen, setFollowUpModalIsOpen] = useState(false);
  const [latestFollowUpId, setLatestFollowUpId] = useState(null);

  const currentFiltersRef = useRef({
    searchQuery: "",
    collegeSearch: "",
    selectedRange: "",
    fromDate: null,
    toDate: null,
    selection: { reason: "", subReason: "" },
  });

  useEffect(() => {
    currentFiltersRef.current = {
      searchQuery,
      collegeSearch,
      selectedRange,
      fromDate,
      toDate,
      selection,
    };
  }, [searchQuery, collegeSearch, selectedRange, fromDate, toDate, selection]);

  // ────────────────────────────────────────────────
  // URL FILTER BOOTSTRAP
  // On first load, read ?filter= (e.g. from the summary cards:
  // /callback-students?filter=today) and translate it into the internal
  // selectedRange so the page opens pre-filtered.
  // ────────────────────────────────────────────────
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlFilter = (urlParams.get("filter") || "").toLowerCase().trim();

    if (!urlFilter) {
      pendingUrlRangeRef.current = null;
      return;
    }

    const mappedRange = URL_FILTER_MAP[urlFilter];

    if (mappedRange === undefined) {
      pendingUrlRangeRef.current = null;
      return;
    }

    // Set the visual control immediately
    setSelectedRange(mappedRange);
    setIsCustomRange(mappedRange === "custom");

    // Remember it so we can apply once the data has been fetched
    pendingUrlRangeRef.current = mappedRange || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getLatestFollowUpStudentIndex = (students) => {
    if (!students || students.length === 0) return -1;
    let latestIndex = -1;
    let latestDate = null;
    students.forEach((student, index) => {
      const fud = student.follow_up_date?.trim();
      if (!fud || fud === "0000-00-00 00:00:00" || fud === "N/A") return;
      const dateObj = new Date(fud);
      if (isNaN(dateObj.getTime())) return;
      if (!latestDate || dateObj > latestDate) {
        latestDate = dateObj;
        latestIndex = index;
      }
    });
    return latestIndex;
  };

  const headers = {
    Authorization: "Bearer sandeep_university_916",
    "Content-Type": "application/json",
  };

  const fetchStudents = async (reapplyFilters = true) => {
    setLoading(true);
    try {
      const encodedMemberType = sessionStorage.getItem("K3M8X1JQ5VZ4B7PWTL9");
      const memberType = encodedMemberType ? atob(encodedMemberType) : null;
      setMemberType(memberType);
      let response;
      if (memberType === "Admin" || memberType === "Manager") {
        response = await fetch(SummaryApi.getAllcallbackStudents.url, {
          method: SummaryApi.getAllcallbackStudents.method,
          headers: headers,
        });
      } else {
        const encodedEmployeeId = sessionStorage.getItem("H7Z4L9VD2XK5JW8QRYT6");
        const decodedEmployeeId = atob(encodedEmployeeId);
        response = await fetch(SummaryApi.callbackStudents.url, {
          method: SummaryApi.callbackStudents.method,
          headers: headers,
          body: JSON.stringify({ employee_id: decodedEmployeeId }),
        });
      }
      const data = await response.json();
      const students = data?.data || [];
      const updatedStudentsData = students.map((student, index) => ({
        ...student,
        sno: index + 1,
        call_duration: student.call_duration
          ? student.call_duration.replace(/\.\d{6}$/, "")
          : student.call_duration,
      }));
      setStudentsData(updatedStudentsData);
      let newFiltered = updatedStudentsData;
      if (reapplyFilters) {
        // Consume any pending URL-derived range on the first data load
        const urlRange = pendingUrlRangeRef.current;
        const f = currentFiltersRef.current;
        const effectiveRange = urlRange || f.selectedRange;
        pendingUrlRangeRef.current = null;

        newFiltered = filterStudentsInternal(
          updatedStudentsData,
          f.searchQuery,
          effectiveRange,
          f.selection,
          f.collegeSearch,
          f.fromDate,
          f.toDate
        );
        setFilteredStudents(newFiltered);
      } else {
        setFilteredStudents(updatedStudentsData);
      }
      const latestIndex = getLatestFollowUpStudentIndex(updatedStudentsData);
      if (latestIndex !== -1) {
        const latestId = updatedStudentsData[latestIndex].student_id;
        setLatestFollowUpId(latestId);
      } else {
        setLatestFollowUpId(null);
      }
      setHighlightTrigger((prev) => prev + 1);
    } catch (error) {
      toast.error(`Error fetching students: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterStudentsInternal = (
    data,
    search,
    range,
    currentFormData,
    collegeQ,
    fDate,
    tDate
  ) => {
    let filtered = data;
    const timeZone = "Asia/Kolkata";
    const formatToKolkata = (date) =>
      new Intl.DateTimeFormat("en-IN", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(date);

    if (collegeQ) {
      const q = collegeQ.toLowerCase().trim();
      filtered = filtered.filter((s) =>
        s.clg_name?.toLowerCase().includes(q)
      );
    }

    if (currentFormData.reason) {
      const { subReason, reason } = currentFormData;
      filtered = filtered.filter((student) => {
        const effectiveInterest = reasons.includes(student?.is_interested)
          ? "Others"
          : student?.is_interested;
        if (reason === "Call Back") {
          const studentReason = (student.call_back_reason || "").trim().toLowerCase();
          const filterSub = (subReason || "").trim().toLowerCase();
          return (
            effectiveInterest === "Call Back" &&
            student.call_back_reason !== "N/A" &&
            (!filterSub || studentReason.includes(filterSub))
          );
        } else if (reason === "Others") {
          return (
            effectiveInterest !== "Call Back" &&
            student.call_back_reason === "N/A" &&
            (subReason ? student.is_interested === subReason : true)
          );
        }
        return true;
      });
    }

    if (range === "today") {
      const todayFormatted = formatToKolkata(new Date());
      filtered = filtered.filter((student) => {
        const studentDateFormatted = formatToKolkata(
          new Date(student.created_date)
        );
        return studentDateFormatted === todayFormatted;
      });
    } else if (range === "yesterday") {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayFormatted = formatToKolkata(yesterday);
      filtered = filtered.filter((student) => {
        const studentDateFormatted = formatToKolkata(
          new Date(student.created_date)
        );
        return studentDateFormatted === yesterdayFormatted;
      });
    } else if (range === "thisWeek") {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);
      filtered = filtered.filter((student) => {
        const studentDate = new Date(student.created_date);
        return studentDate >= startOfWeek && studentDate <= endOfWeek;
      });
    } else if (range === "lastWeek") {
      const today = new Date();
      const startOfCurrentWeek = new Date(today);
      startOfCurrentWeek.setDate(today.getDate() - today.getDay());
      startOfCurrentWeek.setHours(0, 0, 0, 0);
      const endOfLastWeek = new Date(startOfCurrentWeek);
      endOfLastWeek.setDate(startOfCurrentWeek.getDate() - 1);
      endOfLastWeek.setHours(23, 59, 59, 999);
      const startOfLastWeek = new Date(endOfLastWeek);
      startOfLastWeek.setDate(endOfLastWeek.getDate() - 6);
      startOfLastWeek.setHours(0, 0, 0, 0);
      filtered = filtered.filter((student) => {
        const studentDate = new Date(student.created_date);
        return studentDate >= startOfLastWeek && studentDate <= endOfLastWeek;
      });
    } else if (range === "custom") {
      const effectiveFrom = fDate;
      const effectiveTo = tDate;
      if (!effectiveFrom || !effectiveTo) {
        return [];
      }
      const fromFormatted = new Date(effectiveFrom);
      fromFormatted.setHours(0, 0, 0, 0);
      const toFormatted = new Date(effectiveTo);
      toFormatted.setHours(23, 59, 59, 999);
      filtered = filtered.filter((student) => {
        const studentDate = new Date(student.created_date);
        return studentDate >= fromFormatted && studentDate <= toFormatted;
      });
    }

    if (search) {
      const q = search.toLowerCase().trim();
      filtered = filtered.filter(
        (s) =>
          s.student_name?.toLowerCase().includes(q) ||
          s.mobile_number1?.toLowerCase().includes(q) ||
          (["Admin", "Manager"].includes(memberType) &&
            s.employee_name?.toLowerCase().includes(q))
      );
    }
    return filtered;
  };

  const filterStudents = (
    filter,
    search,
    range,
    currentFormData,
    collegeQ,
    fDate = null,
    tDate = null
  ) => {
    const newFiltered = filterStudentsInternal(
      studentsData,
      search,
      range,
      currentFormData,
      collegeQ,
      fDate || fromDate,
      tDate || toDate
    );
    setFilteredStudents(newFiltered);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    filterStudents("All", value, selectedRange, selection, collegeSearch);
  };

  const handleCollegeSearchChange = (e) => {
    const value = e.target.value;
    setCollegeSearch(value);
    filterStudents("All", searchQuery, selectedRange, selection, value);
  };

  const handleChangeRadio = (e) => {
    const val = e.target.value;
    const newSel = { ...selection, reason: val, subReason: "" };
    setSelection(newSel);
    filterStudents("All", searchQuery, selectedRange, newSel, collegeSearch);
  };

  const handleDropdownChange = (e) => {
    const newSel = { ...selection, subReason: e.target.value };
    setSelection(newSel);
    filterStudents("All", searchQuery, selectedRange, newSel, collegeSearch);
  };

  const handleRangeChange = (e) => {
    const range = e.target.value;
    setSelectedRange(range);
    setIsCustomRange(range === "custom");
    if (range !== "custom") {
      setFromDate(null);
      setToDate(null);
      filterStudents("All", searchQuery, range, selection, collegeSearch, null, null);
    } else {
      setFilteredStudents([]);
    }
  };

  const handleResetAllFilters = () => {
    pendingUrlRangeRef.current = null;
    setSearchQuery("");
    setCollegeSearch("");
    setSelectedCourses([]);
    setDropdownValue("");
    setSelection({ reason: "", subReason: "" });
    setSelectedRange("");
    setIsCustomRange(false);
    setFromDate(null);
    setToDate(null);
    setSearchDistrict("");
    setSearchCity("");
    setSearchCollege("");
    setFilteredStudents(studentsData);
    gotoPage(0);
  };

  const isPastOrToday = (dateString) => {
    if (!dateString) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDate = new Date(dateString);
    targetDate.setHours(0, 0, 0, 0);
    return targetDate <= today;
  };

  const columns = React.useMemo(
    () => [
      { Header: "SNO", Cell: ({ row }) => row.index + 1 },
      ...(["Admin", "Manager"].includes(memberType)
        ? [{ Header: "Employee Name", accessor: "employee_name" }]
        : []),
      { Header: "Student Name", accessor: "student_name" },
      { Header: "District", accessor: "district" },
      { Header: "City", accessor: "city" },
      { Header: "College Name", accessor: "clg_name" },
      {
        Header: "Address",
        accessor: "address",
        Cell: ({ value }) => {
          if (!value) return <span className="text-gray-400">NA</span>;
          return (
            <div className="relative group max-w-[100px]">
              <span className="block truncate cursor-default text-gray-700">
                {value}
              </span>
              {/* Hover tooltip chip */}
              <div
                className="pointer-events-none absolute left-0 top-full z-30 mt-1 hidden group-hover:block
                     w-max max-w-[300px] bg-gray-900 text-white text-xs rounded-md px-2 py-1
                     shadow-lg whitespace-normal break-words"
              >
                {value}
              </div>
            </div>
          );
        },
      },
      { Header: "Mobile Number", accessor: "mobile_number1" },
      {
        Header: "Call Duration",
        accessor: "call_duration",
        Cell: ({ value }) =>
          value?.includes(":") ? value.split(".")[0] : value,
      },
      { Header: "Course", accessor: "course" },
      {
        Header: "Previous Status",
        accessor: "prevs_is_interested",
        Cell: ({ value }) => {
          if (!value) value = "NA";
          let colorClass = "text-gray-600";
          if (value === "Most Interested") colorClass = "text-green-600";
          else if (value === "Interested") colorClass = "text-yellow-500";
          else if (value === "Not Interested") colorClass = "text-red-500";
          return <span className={`font-semibold ${colorClass}`}>{value}</span>;
        },
      },
      {
        Header: "Current Status",
        accessor: "is_interested",
        Cell: ({ value }) => {
          let color = "text-gray-600";
          if (value === "Most Interested") color = "text-green-600";
          else if (value === "Interested") color = "text-yellow-500";
          else if (value === "Not Interested") color = "text-red-500";
          return <span className={`font-semibold ${color}`}>{value}</span>;
        },
      },
      {
        Header: "Previous Reason",
        accessor: "prevs_reason",
        Cell: ({ row }) => {
          const { prevs_call_back_reason } = row.original;
          if (!prevs_call_back_reason) return <span>NA</span>;
          return <span>{prevs_call_back_reason}</span>;
        },
      },
      {
        Header: "Current Reason",
        accessor: "call_back_reason",
      },
      {
        Header: "Enrolled Date",
        accessor: "enroll_date",
        Cell: ({ row }) => {
          const { created_date } = row.original;
          return <span>{formatDate(created_date)}</span>;
        },
      },
      {
        Header: "Follow Up",
        accessor: "follow_up_date",
        Cell: ({ row }) => {
          const { follow_up_date } = row.original;

          const isValidFollowUp =
            follow_up_date &&
            follow_up_date !== "N/A" &&
            follow_up_date !== "0000-00-00 00:00:00" &&
            follow_up_date !== "0000-00-00 00:00:00.000000" &&
            !isNaN(new Date(follow_up_date).getTime());

          return (
            <div className="flex flex-col items-start gap-1 min-w-[110px]">
              <span className="text-xs font-medium text-gray-700">
                {isValidFollowUp ? formatDate(follow_up_date) : "NA"}
              </span>
            </div>
          );
        },
      },

      {
        Header: "Next Follow Up",
        accessor: "next_follow_up_date",
        Cell: ({ row }) => {
          const { next_follow_up_date } = row.original;

          const isValidFollowUp =
            next_follow_up_date &&
            next_follow_up_date !== "N/A" &&
            next_follow_up_date !== "0000-00-00 00:00:00" &&
            next_follow_up_date !== "0000-00-00 00:00:00.000000" &&
            !isNaN(new Date(next_follow_up_date).getTime());

          return (
            <div className="flex flex-col items-start gap-1 min-w-[110px]">
              <span className="text-xs font-medium text-gray-700">
                {isValidFollowUp ? formatDateNext(next_follow_up_date) : "NA"}
              </span>
            </div>
          );
        },
      },

      ...(["Employee", "Consultant", "Digital Leads"].includes(memberType)
        ? [
          {
            Header: "Edit",
            accessor: "edit",
            Cell: ({ row }) => (
              <button
                style={{ backgroundColor: THEME.actionBlue }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionBlueHover)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionBlue)}
                className="text-white px-3 py-1.5 rounded transition-colors shadow-sm inline-flex items-center justify-center gap-1 whitespace-nowrap"
                onClick={() => {
                  openModal(row.original, "edit");
                  setLastEditedStudentId(row.original.student_id);
                }}
              >
                <AiOutlineEdit className="shrink-0" />
                <span>Edit</span>
              </button>
            ),
          },
        ]
        : []),
      {
        Header: "View",
        accessor: "view",
        Cell: ({ row }) => (
          <button
            style={{ backgroundColor: THEME.actionYellow }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = THEME.actionYellowHover)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = THEME.actionYellow)}
            className="text-white px-3 py-1.5 rounded transition-colors shadow-sm inline-flex items-center justify-center gap-1 whitespace-nowrap"
            onClick={() => openModal(row.original, "view")}
          >
            <AiOutlineEye className="shrink-0" />
            <span>View</span>
          </button>
        ),
      },
    ],
    [memberType]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { pageIndex, pageSize },
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    pageCount,
  } = useTable(
    {
      columns,
      data: filteredStudents,
      initialState: { pageIndex: 0 },
      autoResetPage: false,
    },
    usePagination
  );

  useEffect(() => {
    gotoPage(0);
  }, [
    searchQuery,
    collegeSearch,
    selectedRange,
    fromDate,
    toDate,
    selection.reason,
    selection.subReason,
    gotoPage,
  ]);

  useEffect(() => {
    if (pageCount > 0 && pageIndex >= pageCount) {
      gotoPage(pageCount - 1);
    } else if (pageCount === 0 && pageIndex !== 0) {
      gotoPage(0);
    }
  }, [pageCount, pageIndex, gotoPage]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return isNaN(date)
      ? "NA"
      : date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
  };

  const formatDateNext = (dateString) => {
    const date = new Date(dateString);
    return isNaN(date)
      ? "NA"
      : date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true, // set to false for 24-hour format
      });
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setIsUpdate(false);
    setIsUpload(false);
  };

  const openModal = (student = null, mode = "view") => {
    const sanitizeStudentData = (data) => {
      const sanitizedData = { ...data };
      Object.keys(sanitizedData).forEach((key) => {
        if (sanitizedData[key] === "N/A") sanitizedData[key] = "";
      });
      return sanitizedData;
    };

    // ── Resolve next_follow_up_date ──────────────────────────────────
    // Treat any MySQL zero-date ("0000-00-00", "0000-00-00 00:00:00",
    // "0000-00-00 00:00:00.000000"), "N/A", or missing value as EMPTY.
    // Do NOT fall back to "now" — the DatePicker must open blank.
    const parseFollowUp = (raw) => {
      if (raw === null || raw === undefined) return null;
      if (dayjs.isDayjs(raw)) {
        return raw.isValid() && raw.year() > 0 ? raw : null;
      }
      const str = String(raw).replace(/[\u200B-\u200D\uFEFF]/g, "").trim();
      if (!str) return null;
      if (str === "N/A") return null;
      if (/^0{4}[-/]0{2}[-/]0{2}/.test(str)) return null; // zero-date

      const strict = dayjs(str, "YYYY-MM-DD HH:mm:ss", true);
      if (strict.isValid() && strict.year() > 0) return strict;

      const loose = dayjs(str);
      return loose.isValid() && loose.year() > 0 ? loose : null;
    };

    const parsedFollowUp = parseFollowUp(student?.next_follow_up_date);
    const initialNextFollowUp = parsedFollowUp
      ? parsedFollowUp.format("YYYY-MM-DD HH:mm:ss")
      : "";

    const payload = {
      ...student,
      is_interested: reasons.includes(student?.is_interested)
        ? "Others"
        : student?.is_interested,
      other_reasons: student?.is_interested,
      call_duration: student?.call_duration || "00:00:00.000000",
      is_jee: student?.is_jee || "",
      is_jee2: student?.is_jee2 || "",
      prev_call_back_reason: student?.call_back_reason || "",
      next_follow_up_date: initialNextFollowUp,
      lead_type: student?.lead_type || "",
      native_district: student?.native_district || "",
      native_address: student?.native_address || "",
    };

    const sanitized = sanitizeStudentData(payload);
    const [timePart] = sanitized.call_duration.split(".");
    const [h, m, s] = timePart.split(":");
    setFormData({ ...sanitized, hours: h, minutes: m, seconds: s });

    if (mode === "edit") {
      setIsUpdate(true);
      setSelectedCourses(sanitized.ug_pg_course?.split(", ") || []);
    }
    setSelectedStudent(student);
    setModalIsOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingSubmit(true);
    try {
      const encodedEmployeeId = sessionStorage.getItem("H7Z4L9VD2XK5JW8QRYT6");
      const decodedEmployeeId = atob(encodedEmployeeId);

      let effective_is_interested;
      let effective_call_back_reason;
      if (formData.is_interested === "Others") {
        effective_is_interested = formData.other_reasons;
        effective_call_back_reason = "N/A";
      } else if (formData.is_interested === "Call Back") {
        effective_is_interested = "Call Back";
        effective_call_back_reason = formData.call_back_reason;
      } else {
        effective_is_interested = formData.is_interested;
        effective_call_back_reason = "N/A";
      }

      let is_jee_value = "";
      if (formData.is_jee === "Jee Mains Results") {
        is_jee_value = "Jee Mains Results";
      } else if (["Interested", "Not Interested", "Most Interested"].includes(formData.is_interested)) {
        if (formData.prev_call_back_reason === "Jee Mains Results") {
          is_jee_value = "Jee Mains Results";
        }
      }

      let is_jee2_value = "";
      if (formData.is_jee2 === "Jee Mains-2 Results") {
        is_jee2_value = "Jee Mains-2 Results";
      } else if (["Interested", "Not Interested", "Most Interested"].includes(formData.is_interested)) {
        if (formData.prev_call_back_reason === "Jee Mains-2 Results") {
          is_jee2_value = "Jee Mains-2 Results";
        }
      }

      const { next_follow_up_date, native_district, native_address, ...restFormData } = formData;

      const payload = {
        ...restFormData,
        student_id: selectedStudent?.student_id,
        employee_id: decodedEmployeeId,
        is_interested: effective_is_interested,
        call_back_reason: effective_call_back_reason,
        ug_pg_course: selectedCourses.join(", "),
        follow_up_date: new Date().toISOString(),
        is_jee: is_jee_value,
        is_jee2: is_jee2_value,
        lead_type: formData.lead_type || selectedStudent?.lead_type || "",
        native_district: formData.native_district || "N/A",
        native_address: formData.native_address || "N/A"
      };


      // Only pass next_follow_up_date if a valid value is selected
      if (formData.next_follow_up_date) {
        payload.next_follow_up_date = dayjs(formData.next_follow_up_date).isValid()
          ? dayjs(formData.next_follow_up_date).format("YYYY-MM-DD HH:mm:ss")
          : formData.next_follow_up_date;
      }

      const response = await fetch(
        isUpdate ? SummaryApi.updateStudent.url : SummaryApi.uploadStudent.url,
        {
          method: "POST",
          headers: headers,
          body: JSON.stringify(payload),
        }
      );
      const result = await response.json();
      if (result.status) {
        toast.success("Success");
        setUpdatedData(payload);
        closeModal();
        setLastEditedStudentId(selectedStudent?.student_id);
        await fetchStudents(true);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleSelectChange = (e) => {
    if (e.target.value && !selectedCourses.includes(e.target.value))
      setSelectedCourses([...selectedCourses, e.target.value]);
  };

  const handleRemoveTag = (c) =>
    setSelectedCourses(selectedCourses.filter((i) => i !== c));

  // ────────────────────────────────────────────────
  // Auto-scroll + highlight
  // ────────────────────────────────────────────────
  useEffect(() => {
    if (highlightTrigger === prevHighlightTrigger.current) return;
    prevHighlightTrigger.current = highlightTrigger;
    if (!tableRef.current) return;

    const scrollToStudent = (studentId, highlightClasses, timeoutMs) => {
      if (!studentId) return;
      const index = filteredStudents.findIndex((s) => s.student_id === studentId);
      if (index === -1) return;
      const targetPage = Math.floor(index / pageSize);
      if (pageIndex !== targetPage) {
        gotoPage(targetPage);
        setTimeout(() => {
          const row = Array.from(tableRef.current.querySelectorAll("tbody tr")).find(
            (tr) => tr.getAttribute("data-student-id") === studentId
          );
          if (row) {
            row.scrollIntoView({ behavior: "smooth", block: "center" });
            row.classList.add(...highlightClasses.split(" "));
            setTimeout(() => {
              row.classList.remove(...highlightClasses.split(" "));
            }, timeoutMs);
          }
        }, 150);
        return;
      }
      const row = Array.from(tableRef.current.querySelectorAll("tbody tr")).find(
        (tr) => tr.getAttribute("data-student-id") === studentId
      );
      if (row) {
        row.scrollIntoView({ behavior: "smooth", block: "center" });
        row.classList.add(...highlightClasses.split(" "));
        setTimeout(() => {
          row.classList.remove(...highlightClasses.split(" "));
        }, timeoutMs);
      }
    };

    if (lastEditedStudentId) {
      scrollToStudent(
        lastEditedStudentId,
        "bg-blue-100 border-2 border-blue-500 font-medium",
        3500
      );
    } else if (latestFollowUpId && latestFollowUpId !== prevLatestFollowUpId.current) {
      scrollToStudent(
        latestFollowUpId,
        "bg-yellow-200 border-4 border-yellow-500",
        5000
      );
      prevLatestFollowUpId.current = latestFollowUpId;
    }
  }, [highlightTrigger, pageIndex, pageSize, gotoPage, lastEditedStudentId, latestFollowUpId]);

  return (
    <div className="mx-auto p-4">
      {loading ? (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-20 flex justify-center items-center text-white">
          Fetching Data...
        </div>
      ) : (
        <>
          <div className="flex justify-between items-center mb-6">
            <h1
              className="text-3xl font-bold"
              style={{ color: THEME.primary }}
            >
              Call Back Students
            </h1>
            <button
              onClick={() => setSearchModalIsOpen(true)}
              style={{ backgroundColor: THEME.primary }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              className="text-white px-4 py-2 rounded-lg transition-opacity"
            >
              Advanced Search
            </button>
          </div>

          <div className="flex flex-wrap gap-4 items-end mb-6">
            <div className="flex gap-4 bg-gray-100 p-2 rounded-lg">
              <label
                className="flex items-center text-sm font-semibold"
                style={{ color: THEME.primary }}
              >
                <input
                  type="radio"
                  value="Call Back"
                  checked={selection.reason === "Call Back"}
                  onChange={handleChangeRadio}
                  className="mr-1"
                />{" "}
                Call Back
              </label>
              <select
                disabled={selection.reason !== "Call Back"}
                value={selection.subReason}
                onChange={handleDropdownChange}
                className="border p-1 rounded"
              >
                <option value="">Select Reason</option>
                <option value="IPE Exams">IPE Exams</option>
                <option value="Jee Mains Results">JEE Mains Results</option>
                <option value="Jee Mains-2 Results">JEE Mains-2 Results</option>
                <option value="EAMCET RESULTS">EAMCET RESULTS</option>
                <option value="IPE Results">IPE Results</option>
                <option value="Supply Exam">Supply Exam</option>
                <option value="Supply Results">Supply Results</option>
                <option value="EOD">EOD</option>
              </select>
              <label
                className="flex items-center text-sm font-semibold"
                style={{ color: THEME.primary }}
              >
                <input
                  type="radio"
                  value="Others"
                  checked={selection.reason === "Others"}
                  onChange={handleChangeRadio}
                  className="mr-1"
                />{" "}
                Others
              </label>
              <select
                disabled={selection.reason !== "Others"}
                value={selection.subReason}
                onChange={handleDropdownChange}
                className="border p-1 rounded"
              >
                <option value="">Select Status</option>
                {reasons.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            <div className="w-64">
              <label
                className="block text-sm font-semibold mb-1"
                style={{ color: THEME.primary }}
              >
                Filter by College
              </label>
              <input
                type="text"
                value={collegeSearch}
                onChange={handleCollegeSearchChange}
                placeholder="e.g. Sri Chaitanya"
                className="w-full border p-2 rounded-lg focus:ring-2"
                style={{ outlineColor: THEME.primary }}
              />
            </div>
            <div className="w-64">
              <label
                className="block text-sm font-semibold mb-1"
                style={{ color: THEME.primary }}
              >
                General Search
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Name or Mobile"
                className="w-full border p-2 rounded-lg focus:ring-2"
                style={{ outlineColor: THEME.primary }}
              />
            </div>
            <div>
              <label
                className="block text-sm font-semibold mb-1"
                style={{ color: THEME.primary }}
              >
                Range
              </label>
              <select
                onChange={handleRangeChange}
                value={selectedRange}
                style={{ backgroundColor: THEME.primary }}
                className="text-white p-2 rounded"
              >
                <option value="">All Time</option>
                <option value="today">Today</option>
                <option value="thisWeek">This Week</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            {isCustomRange && (
              <div className="flex gap-2 bg-blue-50 p-2 border border-blue-200 rounded">
                <div className="flex flex-col">
                  <label
                    className="text-[10px] font-bold"
                    style={{ color: THEME.primary }}
                  >
                    FROM
                  </label>
                  <DatePicker
                    selected={fromDate}
                    onChange={(date) => {
                      setFromDate(date);
                      filterStudents(
                        "All",
                        searchQuery,
                        "custom",
                        selection,
                        collegeSearch,
                        date,
                        toDate
                      );
                    }}
                    className="p-1 text-sm border rounded w-28"
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Start Date"
                  />
                </div>
                <div className="flex flex-col">
                  <label
                    className="text-[10px] font-bold"
                    style={{ color: THEME.primary }}
                  >
                    TO
                  </label>
                  <DatePicker
                    selected={toDate}
                    onChange={(date) => {
                      setToDate(date);
                      filterStudents(
                        "All",
                        searchQuery,
                        "custom",
                        selection,
                        collegeSearch,
                        fromDate,
                        date
                      );
                    }}
                    className="p-1 text-sm border rounded w-28"
                    dateFormat="dd/MM/yyyy"
                    placeholderText="End Date"
                    minDate={fromDate}
                  />
                </div>
              </div>
            )}
            <button
              onClick={handleResetAllFilters}
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
            >
              Clear
            </button>
          </div>

          <div
            ref={tableRef}
            className="overflow-x-auto shadow-lg rounded-lg max-h-[70vh] overflow-y-auto"
          >
            <table {...getTableProps()} className="min-w-full bg-white">
              <thead className="bg-gray-200">
                {headerGroups.map((hg) => (
                  <tr {...hg.getHeaderGroupProps()}>
                    {hg.headers.map((col) => (
                      <th
                        {...col.getHeaderProps()}
                        className="p-3 text-left font-semibold whitespace-nowrap"
                        style={{ color: THEME.primary }}
                      >
                        {col.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row);
                  const studentId = row.original.student_id;
                  const isLatestFollowUp = studentId === latestFollowUpId;
                  const followUpDue =
                    memberType === "Employee" &&
                    isPastOrToday(row.original.follow_up_date) &&
                    !isLatestFollowUp;
                  let rowClass = "hover:bg-gray-100 transition";
                  if (isLatestFollowUp) {
                    rowClass += " bg-yellow-100 hover:bg-yellow-200";
                  } else if (followUpDue) {
                    rowClass += " bg-green-200 hover:bg-green-300";
                  }

                  return (
                    <tr
                      {...row.getRowProps()}
                      data-student-id={studentId}
                      className={rowClass}
                    >
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()} className="p-3 border-t">
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center mb-4 mt-2 flex-wrap gap-4">
            <span
              className="text-sm font-medium"
              style={{ color: THEME.primary }}
            >
              Showing {page.length} of {filteredStudents.length} entries
              {" • Page "}{pageIndex + 1} of {pageCount || 1}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
                style={{ color: THEME.primary }}
                className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                {"<< First"}
              </button>
              <button
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
                style={{ color: THEME.primary }}
                className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                {"< Prev"}
              </button>
              <button
                onClick={() => nextPage()}
                disabled={!canNextPage}
                style={{ color: THEME.primary }}
                className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                {"Next >"}
              </button>
              <button
                onClick={() => gotoPage(pageCount - 1)}
                disabled={!canNextPage}
                style={{ color: THEME.primary }}
                className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                {"Last >>"}
              </button>
            </div>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                gotoPage(0);
              }}
              style={{ color: THEME.primary }}
              className="border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2"
            >
              {[5, 10, 15, 20, 25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>
          </div>
        </>
      )}

      <StudentsModal
        modalIsOpen={modalIsOpen}
        closeModal={closeModal}
        isUpdate={isUpdate}
        isUpload={isUpload}
        formData={formData}
        setFormData={setFormData}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        loadingSubmit={loadingSubmit}
        selectedCourses={selectedCourses}
        setSelectedCourses={setSelectedCourses}
        handleSelectChange={handleSelectChange}
        handleRemoveTag={handleRemoveTag}
        memberType={memberType}
        selectedStudent={selectedStudent}
      />

      <FollowUpModal
        isOpen={followUpModalIsOpen}
        onClose={() => setFollowUpModalIsOpen(false)}
        student={selectedStudent}
        onSuccess={() => {
          fetchStudents(true);
          setFollowUpModalIsOpen(false);
        }}
      />

      <Modal
        isOpen={searchModalIsOpen}
        onRequestClose={() => {
          setSearchModalIsOpen(false);
          setSearchDistrict("");
          setSearchCity("");
          setSearchCollege("");
        }}
        contentLabel="Search Filters"
        className="mx-auto my-10 w-[30%] mt-[100px] max-w-md bg-white rounded-lg shadow-lg relative z-50"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-start"
      >
        <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-slate-100">
          <div className="flex justify-between items-center">
            <h2
              className="text-xl font-semibold"
              style={{ color: THEME.primary }}
            >
              Search Filters
            </h2>
            <button onClick={() => setSearchModalIsOpen(false)}>
              <AiOutlineClose className="w-6 h-6 text-gray-600 hover:text-gray-800" />
            </button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex flex-col">
            <label
              htmlFor="district-filter"
              className="mb-1 font-semibold text-sm"
              style={{ color: THEME.primary }}
            >
              District
            </label>
            <select
              id="district-filter"
              className="p-2 border rounded"
              value={searchDistrict}
              onChange={(e) => {
                setSearchDistrict(e.target.value);
                setSearchCity("");
                setSearchCollege("");
              }}
            >
              <option value="">Select District</option>
              {Array.from(
                new Set(filteredStudents.map((student) => student.district))
              )
                .filter(Boolean)
                .map((district) => (
                  <option key={district} value={district}>
                    {district}
                  </option>
                ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label
              htmlFor="city-filter"
              className="mb-1 font-semibold text-sm"
              style={{ color: THEME.primary }}
            >
              City
            </label>
            <select
              id="city-filter"
              className="p-2 border rounded"
              value={searchCity}
              onChange={(e) => {
                setSearchCity(e.target.value);
                setSearchCollege("");
              }}
              disabled={!searchDistrict}
            >
              <option value="">
                {searchDistrict ? "Select City" : "First select District"}
              </option>
              {Array.from(
                new Set(
                  filteredStudents
                    .filter(
                      (student) =>
                        searchDistrict &&
                        student.district?.toLowerCase().includes(searchDistrict.toLowerCase())
                    )
                    .map((student) => student.city)
                )
              )
                .filter(Boolean)
                .map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label
              htmlFor="college-filter"
              className="mb-1 font-semibold text-sm"
              style={{ color: THEME.primary }}
            >
              College Name
            </label>
            <select
              id="college-filter"
              className="p-2 border rounded"
              value={searchCollege}
              onChange={(e) => setSearchCollege(e.target.value)}
              disabled={!searchCity}
            >
              <option value="">
                {searchCity ? "Select College" : "First select City"}
              </option>
              {Array.from(
                new Set(
                  filteredStudents
                    .filter(
                      (student) =>
                        searchCity &&
                        student.city?.toLowerCase().includes(searchCity.toLowerCase())
                    )
                    .map((student) => student.clg_name)
                )
              )
                .filter(Boolean)
                .map((college) => (
                  <option key={college} value={college}>
                    {college}
                  </option>
                ))}
            </select>
          </div>
          <div className="flex justify-end pt-4">
            <button
              onClick={() => {
                setSearchDistrict("");
                setSearchCity("");
                setSearchCollege("");
                setFilteredStudents(studentsData);
                gotoPage(0);
              }}
              className="px-4 py-2 mr-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Clear
            </button>
            <button
              onClick={() => {
                const filtered = studentsData.filter((student) => {
                  const districtMatch =
                    !searchDistrict ||
                    student.district
                      ?.toLowerCase()
                      .includes(searchDistrict.toLowerCase());
                  const cityMatch =
                    !searchCity ||
                    student.city
                      ?.toLowerCase()
                      .includes(searchCity.toLowerCase());
                  const collegeMatch =
                    !searchCollege ||
                    student.clg_name
                      ?.toLowerCase()
                      .includes(searchCollege.toLowerCase());
                  return districtMatch && cityMatch && collegeMatch;
                });
                setFilteredStudents(filtered);
                gotoPage(0);
                setSearchModalIsOpen(false);
              }}
              style={{ backgroundColor: THEME.primary }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              className="px-4 py-2 text-white rounded transition-opacity"
            >
              Apply
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CallBackStudents;